import { SearchQueryBody, ConditionGroup, SimpleCondition } from "@/types";

import { HttpClient, robotoHeaders } from "../../http";

import {
  ActionRecord,
  InvocationRecord,
  InvocationStatus,
  UpdateActionRequest,
} from "./ActionRecord";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
}

export class ActionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async deleteAction(
    name: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "deleteAction requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/actions/${name}`);
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.throwIfError();
  }

  public async getAction(
    name: string,
    digest?: string,
    options?: Partial<Options>,
  ): Promise<ActionRecord> {
    if (!options?.resourceOwnerId) {
      throw Error("getAction requires a resource owner ID, none was provided");
    }

    const searchParams = new URLSearchParams();
    if (digest) {
      searchParams.append("digest", digest);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/${name}`,
      searchParams,
    );

    return this.#httpClient
      .get(requestUrl, {
        headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
        signal: options?.abortSignal,
      })
      .then((response) => response.json<ActionRecord>());
  }

  public async updateAction(
    name: string,
    updates: UpdateActionRequest,
    options?: Partial<Options>,
  ): Promise<ActionRecord> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "updateAction requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/actions/${name}`);

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<ActionRecord>();
  }

  public async queryActiveInvocationsForDataset(
    orgId: string,
    datasetId: string,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/query`,
    );

    const datasetCondition: SimpleCondition = {
      field: "data_source_id",
      comparator: "EQUALS",
      value: datasetId,
    };

    const conditions = [
      InvocationStatus.Completed,
      InvocationStatus.Cancelled,
      InvocationStatus.Failed,
      InvocationStatus.Deadly,
    ].map((status) => {
      const condition: SimpleCondition = {
        field: "last_status",
        comparator: "NOT_EQUALS",
        value: status,
      };

      return condition;
    });

    const nonTerminalCondition: ConditionGroup = {
      conditions: conditions,
      operator: "AND",
    };

    const searchConditions: ConditionGroup = {
      conditions: [datasetCondition, nonTerminalCondition],
      operator: "AND",
    };

    const searchBody: SearchQueryBody = {
      condition: searchConditions,
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal,
      body: JSON.stringify(searchBody),
    });

    return await response.json<{
      items: InvocationRecord[];
      next_token: string | null;
    } | null>();
  }

  public async queryRecentInvocationsForDataset(
    orgId: string,
    datasetId: string,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/query`,
    );

    const datasetCondition: SimpleCondition = {
      field: "data_source_id",
      comparator: "EQUALS",
      value: datasetId,
    };

    const searchConditions: ConditionGroup = {
      conditions: [datasetCondition],
      operator: "AND",
    };

    const searchBody: SearchQueryBody = {
      condition: searchConditions,
      sort_by: "created",
      sort_direction: "DESC",
      // Limit results to avoid fetching too many invocations
      limit: 50,
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal,
      body: JSON.stringify(searchBody),
    });

    return response.json<{
      items: InvocationRecord[];
      next_token: string | null;
    } | null>();
  }
}
